import { DataTable, type DataTableColumn, type RowTone } from "@bruntworkwear-apps/ui";
import { money, quantity, rate, signedMoney } from "@/shared/lib";
import type { PoSummary } from "@/shared/api";
import { AllocationCellView, shortfallOf } from "./components/AllocationCell.view";
import { PoToggleView } from "./components/PoToggle.view";
import { RateSourceCellView } from "./components/RateSourceCell.view";
import styles from "./PoTable.module.css";
import type { PoTableProps } from "./PoTable.types";

/** Which order this is, and how big. */
function identityColumns(
  selectedPo: string | null | undefined,
  onSelect: ((po: string) => void) | undefined,
): DataTableColumn<PoSummary>[] {
  return [
    {
      key: "po",
      header: "PO",
      width: "6.5rem",
      render: (order) =>
        onSelect ? (
          <PoToggleView
            po={order.po}
            isOpen={order.po === selectedPo}
            onToggle={() => onSelect(order.po)}
          />
        ) : (
          order.po
        ),
    },
    { key: "lines", header: "Lines", render: (order) => order.line_count, numeric: true },
    {
      key: "qty",
      header: "Quantity",
      render: (order) => quantity(order.total_quantity),
      numeric: true,
    },
    {
      key: "rate",
      header: "Customs rate",
      render: (order) => rate(order.allocation?.customs_rate ?? null),
      numeric: true,
      secondary: true,
    },
  ];
}

/** What it costs, and how that compares with the tracker. */
function moneyColumns(threshold: number): DataTableColumn<PoSummary>[] {
  return [
    {
      key: "calc",
      header: "Calc customs",
      render: (order) => money(order.calculated_customs),
      numeric: true,
    },
    {
      key: "tracker",
      header: "Tracker customs",
      render: (order) => money(order.tracker_customs),
      numeric: true,
    },
    {
      key: "variance",
      header: "Variance",
      // Same rule as the workbook and the comparison table: green where it ties
      // out, red where an adjustment is needed. A null variance has nothing to
      // tie out against, so it takes neither.
      render: (order) => (
        <span className={varianceClass(order, threshold)}>
          {signedMoney(order.customs_variance)}
        </span>
      ),
      numeric: true,
    },
    {
      key: "landing",
      header: "Calc landing",
      render: (order) => money(order.calculated_landing),
      numeric: true,
      secondary: true,
    },
  ];
}

/** How the rate was arrived at - the audit trail, in two columns. */
function matchColumns(): DataTableColumn<PoSummary>[] {
  return [
    {
      key: "allocation",
      header: "Matched",
      render: (order) => <AllocationCellView order={order} />,
    },
    {
      key: "source",
      header: "Source",
      render: (order) => <RateSourceCellView order={order} />,
      secondary: true,
    },
  ];
}

/** True when this order's customs variance is worth an adjustment. */
function isAdjustable(order: PoSummary, threshold: number): boolean {
  return order.customs_variance !== null && Math.abs(order.customs_variance) >= threshold;
}

/** Green where the variance ties out, red where it needs adjusting, plain when unknown. */
function varianceClass(order: PoSummary, threshold: number): string | undefined {
  if (order.customs_variance === null) return undefined;
  return isAdjustable(order, threshold) ? styles.adjust : styles.tiesOut;
}

/**
 * Per purchase order, with the tie-out against the tracker.
 *
 * Row tone carries the judgment: red where the tracker could not explain the
 * quantity at all, amber where the variance is worth adjusting.
 */
export function PoTable({ orders, threshold, selectedPo, onSelect }: PoTableProps) {
  const tone = (order: PoSummary): RowTone => {
    // Selection wins over severity: the expanded order should be findable at a
    // glance once the breakdown below has pushed it out of the eyeline.
    if (order.po === selectedPo) return "success";
    if (shortfallOf(order) > 0 || order.allocation?.rate_source === "unavailable") return "danger";
    return isAdjustable(order, threshold) ? "warning" : "default";
  };

  return (
    <DataTable
      rows={orders}
      columns={[
        ...identityColumns(selectedPo, onSelect),
        ...moneyColumns(threshold),
        ...matchColumns(),
      ]}
      rowKey={(order) => order.po}
      rowTone={tone}
      maxHeight="34rem"
      emptyMessage="No purchase orders were costed."
    />
  );
}
